import React, {useState, Fragment, useEffect, useRef} from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useGenerateOtpMutation, useValidateOtpMutation, useRegisterMutation, useEditUserInformationMutation } from "../../../redux/api/users.api";
import { removePopup, setUserData, addPopup, getUserData, setUserRequestDone } from "../../../redux/actions";
import { setTokens } from "../../../redux/slices/tokenSlice";
import { executeWithCaptcha } from '../../../app/functions';
import AnalyticsManager from '../../../app/analytics';
import * as popups from '../../../constants/popup-types';
import './index.scss';
import Loader from 'components/common/loader';
import { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';

const getOtpMessage = (response) => response?.message || response?.data?.message || '';

const PhoneValidation = ({ isLogin, phone, userId, email, action, data, loginVerification, btnText, otpResponseData, process }) => {
    const [firstTry, setFirstTry] = useState(true);
    const [allValid, setAllValid] = useState(false);
    const [disableMainBtn, setDisableMainBtn] = useState(false);
    const [form, setForm] = useState({
        first: false,
        second: false,
        third: false,
        fourth: false,
        fifth: false,
        sixth: false
    });
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const [otpError, setOtpError] = useState('');
    const [sendTimer, setSendTimer] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasAutoFocused, setHasAutoFocused] = useState(false);
    const [localOtpResponseData, setLocalOtpResponseData] = useState(null);
    const [loginMessage, setLoginMessage] = useState('');
    const inputRefs = useRef([]);
    const countdownRef = useRef(null);
    const autoSubmitOtpTimeoutRef = useRef(null);
    const validateOtpInFlightRef = useRef(false);

    const activeOtpResponseData = localOtpResponseData || otpResponseData;
    const canSendEmailOtp = activeOtpResponseData?.isEmailValid ?? activeOtpResponseData?.data?.isEmailValid ?? false;

    const scheduleOtpAutoSubmit = (run, delayMs) => {
        if (autoSubmitOtpTimeoutRef.current) {
            clearTimeout(autoSubmitOtpTimeoutRef.current);
        }
        autoSubmitOtpTimeoutRef.current = setTimeout(() => {
            autoSubmitOtpTimeoutRef.current = null;
            run();
        }, delayMs);
    };

    const dispatch = useDispatch();
    const deviceState = useSelector((state) => state.state.deviceState);
    const text = useSelector((state) => state.config.Translations);
    const otpResponse = useSelector((state) => state.data.OtpResponse);
    const lockDown = useSelector((state) => state.config.LockDownErr);
    const userData = useSelector((state) => state.config.UserData);
    const parameters = useSelector((state) => state.config.Parameters);
    
    const [generateOtpMutation, { isLoading: isGenerateOtpLoading }] = useGenerateOtpMutation();
    const [validateOtpMutation, { isLoading: isValidateOtpLoading }] = useValidateOtpMutation();
    const [registerMutation, { isLoading: isRegisterLoading }] = useRegisterMutation();
    const [editUserInformation] = useEditUserInformationMutation();

    const focusInput = (index) => {
        if (inputRefs.current[index]) {
            try {
                inputRefs.current[index].focus();
                inputRefs.current[index].select();
                return;
            } catch (error) {
            }
        }
        
        const inputElement = document.getElementById(`otp-${index}`);
        if (inputElement) {
            const actualInput = inputElement.querySelector('input');
            if (actualInput) {
                try {
                    actualInput.focus();
                    actualInput.select();
                } catch (error) {
                }
            }
        }
        
        const firstField = document.querySelector('[id="otp-0"] input');
        if (firstField) {
            try {
                firstField.focus();
                firstField.select();
            } catch (error) {
            }
        }
    };

    const forceFocusFirstInput = () => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
            inputRefs.current[0].select();
            return;
        }
        
        const inputElement = document.getElementById('otp-0');
        if (inputElement) {
            const actualInput = inputElement.querySelector('input');
            if (actualInput) {
                actualInput.focus();
                actualInput.select();
                return;
            }
        }
        
        const textField = document.getElementById('otp-0');
        if (textField) {
            textField.focus();
            return;
        }
        
        const firstField = document.querySelector('[id="otp-0"] input');
        if (firstField) {
            firstField.focus();
            firstField.select();
        }
    };

    useEffect(() => {
        if (otpResponse.returned) {
            if (otpResponse.answer) {
                if (typeof action === 'function') {
                    action(data);
                }
                dispatch(removePopup());
            } else {
                if (process !== 'register') {
                    setFirstTry(false);
                }
            }
            setDisableMainBtn(false);
        }
    }, [otpResponse, action, data, dispatch, process]);

    useEffect(() => {
        if (parameters && parameters?.sms_verification_send_code_again__visible_in_sec) {
            const countdownValue = parseInt(parameters?.sms_verification_send_code_again__visible_in_sec) || 5;
            setCountdown(countdownValue);
        }
    }, [parameters]);

    useEffect(() => {
        const messageSource = localOtpResponseData || otpResponseData;
        if (messageSource) {
            setLoginMessage(getOtpMessage(messageSource));
        }
    }, [otpResponseData, localOtpResponseData]);

    useEffect(() => {
        return () => {
            if (autoSubmitOtpTimeoutRef.current) {
                clearTimeout(autoSubmitOtpTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (countdown > 0) {
            countdownRef.current = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        } else if (countdown === 0 && !sendTimer) {
            setSendTimer(true);
        }

        return () => {
            if (countdownRef.current) {
                clearTimeout(countdownRef.current);
            }
        };
    }, [countdown, sendTimer]);

    const handleOnInput = (e) => {
        if (e.target.previousSibling !== null) {
            e.target.previousSibling.focus();
        }
        e.target.value = e.target.value.slice(0, 1);
        let newForm;
        if (e.target.value === '')
            newForm = {...form, [e.target.name]: false};
        else
            newForm = {...form, [e.target.name]: e.target.value};

        let is_valid = true;
        let filledFields = 0;
        for (let key in newForm) {
            if (newForm[key] === false) {
                is_valid = false;
            } else {
                filledFields++;
            }
        }

        setForm(newForm);
        setAllValid(is_valid);

        if (is_valid && filledFields === 6) {
            let otpValue = '';
            for (let key in newForm) {
                if (newForm[key] !== false && newForm[key] !== '') {
                    otpValue = otpValue.concat(newForm[key]);
                }
            }
            if (otpValue.length === 6) {
                scheduleOtpAutoSubmit(() => handleSubmitWithOtp(otpValue), 100);
            }
        }
    };

    const handleSubmitWithOtp = async (otpValue) => {
        if (autoSubmitOtpTimeoutRef.current) {
            clearTimeout(autoSubmitOtpTimeoutRef.current);
            autoSubmitOtpTimeoutRef.current = null;
        }

        if (
            validateOtpInFlightRef.current ||
            disableMainBtn ||
            isLoading ||
            isValidateOtpLoading ||
            isRegisterLoading
        ) {
            return;
        }

        const isOtpValid = otpValue.length === 6;
        if (!isOtpValid) {
            setOtpError(text?.['phone_validation_err'] || 'אנא הזן 6 ספרות');
            setFirstTry(false);
            return;
        }

        const userIdToUse = userId || userData?.user_id || localOtpResponseData?.userId || otpResponseData?.userId || otpResponse?.userId;
        const validationToken = localOtpResponseData?.validationToken || localOtpResponseData?.token || localOtpResponseData?.validation_token || 
                              otpResponseData?.validationToken || otpResponseData?.token || otpResponseData?.validation_token ||
                              otpResponse?.validationToken || otpResponse?.token || otpResponse?.validation_token;
        const sessionId = localOtpResponseData?.sessionId || localOtpResponseData?.session_id || 
                         otpResponseData?.sessionId || otpResponseData?.session_id ||
                         otpResponse?.sessionId || otpResponse?.session_id;

        if (!validationToken || !sessionId) {
            setFirstTry(false);
            return;
        }

        validateOtpInFlightRef.current = true;
        setFirstTry(false);
        setIsLoading(true);
        setDisableMainBtn(true);

        const requestPayload = {
            userId: String(userIdToUse),
            otpCode: otpValue,
            token: validationToken,
            sessionId: sessionId,
        };

        try {
            await new Promise((resolve) => {
                executeWithCaptcha(async (captchaToken) => {
                    try {
                        const payloadWithCaptcha = {
                            ...requestPayload,
                            'g-recaptcha-response': captchaToken
                        };
                        const result = await validateOtpMutation(payloadWithCaptcha).unwrap();

                        if (!result) {
                            throw new Error('No result from validateOtpMutation');
                        }

                        const authToken = result?.authToken;
                        const sessionToken = result?.sessionToken || null;

                        dispatch(setTokens({
                            authToken,
                            sessionToken
                        }));

                        const originalRegistrationPayload = otpResponseData?.registrationPayload || otpResponse?.registrationPayload || data;
                        let registrationPayload = null;

                        if (originalRegistrationPayload) {
                            registrationPayload = { ...originalRegistrationPayload };
                            if (sessionToken) {
                                registrationPayload['sessionToken'] = sessionToken;
                            }
                        }

                        const shouldRegisterNewUser = registrationPayload && process === 'register';
                        const shouldEditUser = registrationPayload && process === 'register' && userData;

                        if (shouldRegisterNewUser) {
                            try {
                                const registrationResult = await registerMutation(registrationPayload).unwrap();

                                let userData = null;
                                if (registrationResult && registrationResult.data) {
                                    userData = registrationResult.data;
                                } else if (registrationResult && registrationResult.user) {
                                    userData = registrationResult.user;
                                } else if (registrationResult) {
                                    userData = registrationResult;
                                }

                                if (userData) {
                                    dispatch(setTokens({
                                        authToken: registrationResult.accessToken || registrationResult.token,
                                    }));
                                    dispatch(setUserData(userData));
                                    AnalyticsManager.updateUserProperties(userData);
                                    AnalyticsManager.logRegister(userData);

                                    dispatch(setUserRequestDone(true));

                                    dispatch(removePopup());

                                    setTimeout(() => {
                                        dispatch(addPopup({
                                            type: popups.GENERAL_MESSAGE,
                                            text: 'הרשמה הושלמה בהצלחה!'
                                        }));
                                    }, 100);
                                }

                            } catch (registrationError) {
                                setFirstTry(false);
                                setDisableMainBtn(false);
                                return;
                            }
                        } else if (shouldEditUser) {
                            try {
                                if (sessionToken) {
                                    registrationPayload['sessionToken'] = sessionToken;
                                }

                                const editResult = await editUserInformation(registrationPayload).unwrap();

                                if (!editResult){
                                    setFirstTry(false);
                                    setDisableMainBtn(false);
                                    return;
                                }
                                dispatch(setTokens({
                                    authToken: editResult.accessToken || editResult.token,
                                }));
                                dispatch(setUserData(editResult));

                                AnalyticsManager.updateUserProperties(editResult);
                                AnalyticsManager.logRegister(editResult);

                                dispatch(setUserRequestDone(true));

                                dispatch(removePopup());

                                setTimeout(() => {
                                    dispatch(addPopup({
                                        type: popups.GENERAL_MESSAGE,
                                        text: 'פרטייך נשמרו בהצלחה',
                                    }));
                                }, 100);

                            } catch (error) {
                                console.error('Failed to update user information:', error);
                                setFirstTry(false);
                                setDisableMainBtn(false);
                            }
                        } else {
                            if (typeof action === 'function') {
                                action(registrationPayload || data);
                            }
                            dispatch(removePopup());
                        }
                    } catch (validationError) {
                        console.error('OTP validation error:', validationError);
                        setOtpError(text?.['phone_validation_err'] || 'קוד אימות שגוי');
                        setFirstTry(false);
                        setDisableMainBtn(false);
                    } finally {
                        setIsLoading(false);
                        setDisableMainBtn(false);
                        validateOtpInFlightRef.current = false;
                        resolve();
                    }
                }, RECAPTCHA_ACTIONS.VALIDATE_OTP);
            });
        } catch (testError) {
            setOtpError(text?.['phone_validation_err'] || 'קוד אימות שגוי');
            setIsLoading(false);
            setDisableMainBtn(false);
            validateOtpInFlightRef.current = false;
        }
    };

    const handleSubmit = async (e, formData = null) => {
        e.preventDefault();
        
        let otpValue = '';
        
        if (formData) {
            for (let key in formData) {
                if (formData[key] !== false && formData[key] !== '') {
                    otpValue = otpValue.concat(formData[key]);
                }
            }
        } else {
            const currentForm = form;
            for (let key in currentForm) {
                if (currentForm[key] !== false && currentForm[key] !== '') {
                    otpValue = otpValue.concat(currentForm[key]);
                }
            }
            
            if (!otpValue || otpValue.length === 0) {
                otpValue = otpDigits.join('');
            }
        }
        
        await handleSubmitWithOtp(otpValue);
    };

    const handleResendOtp = async (isEmailNeeded = false) => {
        if (sendTimer) {
            setOtpDigits(['', '', '', '', '', '']);
            setOtpError('');
            setFirstTry(true);
            setHasAutoFocused(false);
            
            setTimeout(() => {
                forceFocusFirstInput();
            }, 50);
            
            const userIdToUse = userId || userData?.user_id || otpResponseData?.userId;
            const phoneToUse = phone || userData?.phone;
            const ccDigitsToUse = otpResponseData?.ccDigits;

            let requestPayload = {
                userId: String(userIdToUse),
                cellphone: String(phoneToUse),
                process: process,
                ccDigits: ccDigitsToUse
            };
            if (isEmailNeeded) {
                requestPayload = {
                    ...requestPayload,
                    isEmailNeeded: true
                };
            }

            executeWithCaptcha(async (token) => {
                requestPayload['g-recaptcha-response'] = token;

                try {
                    const result = await generateOtpMutation(requestPayload).unwrap();
                    
                    setLocalOtpResponseData(result);
                    setLoginMessage(getOtpMessage(result));
                    
                    setTimeout(() => {
                        forceFocusFirstInput();
                    }, 200);
                    setTimeout(() => {
                        forceFocusFirstInput();
                    }, 500);
                    setTimeout(() => {
                        forceFocusFirstInput();
                    }, 800);
                    setTimeout(() => {
                        forceFocusFirstInput();
                        setHasAutoFocused(true);
                    }, 1200);
                    
                } catch (error) {
                }
            }, RECAPTCHA_ACTIONS.RESEND_CODE);

            setSendTimer(false);
            const countdownValue = parseInt(parameters?.sms_verification_send_code_again__visible_in_sec) || 5;
            setCountdown(countdownValue);
        }
    };

    const handleEmailOtp = async () => {
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError('');
        setFirstTry(true);
        setHasAutoFocused(false);
        
        setTimeout(() => {
            forceFocusFirstInput();
        }, 50);
        
        const userIdToUse = userId || userData?.user_id || otpResponseData?.userId;
        const phoneToUse = phone || userData?.phone;
        const ccDigitsToUse = otpResponseData?.ccDigits;

        const requestPayload = {
            userId: String(userIdToUse),
            cellphone: String(phoneToUse),
            process: process,
            ccDigits: ccDigitsToUse,
            isEmailNeeded: true
        };

        executeWithCaptcha(async (token) => {
            requestPayload['g-recaptcha-response'] = token;

            try {
                const result = await generateOtpMutation(requestPayload).unwrap();
                setLocalOtpResponseData(result);
                setLoginMessage(getOtpMessage(result));
                
                setTimeout(() => {
                    forceFocusFirstInput();
                }, 200);
                setTimeout(() => {
                    forceFocusFirstInput();
                }, 500);
                setTimeout(() => {
                    forceFocusFirstInput();
                }, 800);
                setTimeout(() => {
                    forceFocusFirstInput();
                    setHasAutoFocused(true);
                }, 1200);
                
            } catch (error) {
            }
        }, RECAPTCHA_ACTIONS.RESEND_CODE);

        setSendTimer(false);
        const countdownValue = parseInt(parameters?.sms_verification_send_code_again__visible_in_sec) || 5;
        setCountdown(countdownValue);
    };

    const handleOtpChange = (index, value) => {
        if (value.length > 1) {
            const digits = value.replace(/\D/g, '').slice(0, 6);
            if (digits.length > 1) {
                const newOtpDigits = [...otpDigits];
                for (let i = 0; i < digits.length && (index + i) < 6; i++) {
                    newOtpDigits[index + i] = digits[i];
                }
                setOtpDigits(newOtpDigits);
                setOtpError('');
                
                const nextEmptyIndex = newOtpDigits.findIndex((digit, i) => i >= (index + digits.length) && digit === '');
                const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(index + digits.length - 1, 5);
                setTimeout(() => {
                    focusInput(focusIndex);
                }, 10);
                
                const otpValue = newOtpDigits.join('');
                if (otpValue.length === 6 && /^\d{6}$/.test(otpValue)) {
                    scheduleOtpAutoSubmit(() => handleSubmitWithOtp(otpValue), 500);
                }
                return;
            } else {
                value = value.slice(-1);
            }
        }
        
        if (!/^\d*$/.test(value)) {
            return;
        }

        const newOtpDigits = [...otpDigits];
        newOtpDigits[index] = value;
        setOtpDigits(newOtpDigits);
        setOtpError('');

        if (value && index < 5) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    focusInput(index + 1);
                }, 50);
            });
        }

        const otpValue = newOtpDigits.join('');
        if (otpValue.length === 6 && /^\d{6}$/.test(otpValue)) {
            scheduleOtpAutoSubmit(() => handleSubmitWithOtp(otpValue), 500);
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!otpDigits[index] && index > 0) {
                e.preventDefault();
                focusInput(index - 1);
            } else if (otpDigits[index]) {
                const newOtpDigits = [...otpDigits];
                newOtpDigits[index] = '';
                setOtpDigits(newOtpDigits);
            }
        }
        
        if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        }
        
        if (e.key === 'ArrowRight' && index < 5) {
            e.preventDefault();
            focusInput(index + 1);
        }
        
        if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            navigator.clipboard.readText().then(text => {
                const pastedDigits = text.replace(/\D/g, '').slice(0, 6);
                if (pastedDigits.length > 0) {
                    const newOtpDigits = [...otpDigits];
                    for (let i = 0; i < pastedDigits.length && i < 6; i++) {
                        newOtpDigits[i] = pastedDigits[i];
                    }
                    setOtpDigits(newOtpDigits);
                    setOtpError('');
                    
                    setTimeout(() => {
                        focusInput(0);
                    }, 10);
                    
                    const otpValue = newOtpDigits.join('');
                    if (otpValue.length === 6 && /^\d{6}$/.test(otpValue)) {
                        scheduleOtpAutoSubmit(() => handleSubmitWithOtp(otpValue), 200);
                    }
                }
            }).catch(err => {
            });
        }
    };

    const processPastedText = (pastedText) => {
        const pastedDigits = pastedText.replace(/\D/g, '').slice(0, 6);
        
        if (pastedDigits.length > 0) {
            const newOtpDigits = [...otpDigits];
            for (let i = 0; i < pastedDigits.length && i < 6; i++) {
                newOtpDigits[i] = pastedDigits[i];
            }
            setOtpDigits(newOtpDigits);
            setOtpError('');
            
            setTimeout(() => {
                focusInput(0);
            }, 10);
            
            const otpValue = newOtpDigits.join('');
            if (otpValue.length === 6 && /^\d{6}$/.test(otpValue)) {
                scheduleOtpAutoSubmit(() => handleSubmitWithOtp(otpValue), 200);
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        
        let pastedText = '';
        
        if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData('text');
        } else if (e.originalEvent && e.originalEvent.clipboardData) {
            pastedText = e.originalEvent.clipboardData.getData('text');
        }
        
        if (!pastedText && navigator.clipboard && navigator.clipboard.readText) {
            navigator.clipboard.readText().then(text => {
                processPastedText(text);
            }).catch(() => {
                processPastedText('');
            });
            return;
        }
        
        processPastedText(pastedText);
    };

    const handleOnKeyUp = (e) => {
        if (e.keyCode === 8) {
            if (e.target.nextSibling !== null) {
                e.target.nextSibling.focus();
            }
        }
    };

    const isMobile = deviceState === 'mobile';
    const buttonText = btnText || text['phone_validation_btn'];
    const isWaitingForOtpResponse = !activeOtpResponseData;

    return (
        <div className='phone-validation'>
            { !isLogin && (
                    <div className='welcome'>
                        <span className='welcome-title'>{text['login_title']}</span>
                        <span className='welcome-subtitle'>{text['login_subtitle']}</span>
                    </div>
            )}

            {isWaitingForOtpResponse ? (
                <div className='otp-loader'>
                    <Loader />
                </div>
            ) : (
            <>
            <div className='top' style={{textAlign: 'center', paddingBottom: isMobile ? '18px' : '24px'}}>
                <span className='title'>{text['phone_validation_title']}</span>
                {loginMessage && (
                    <span className='login-message' dir='rtl'>{loginMessage}</span>
                )}
                {!isMobile && (
                    <span className='subtitle'>{text['phone_validation_subtitle']}</span>
                )}
            </div>

            <form className='validation-form' onSubmit={handleSubmit}>
                <div className='input-container'>
                    <input type='number' name='sixth' pattern="[0-9]*" onKeyUp={handleOnKeyUp}
                           onInput={handleOnInput} tabIndex={6}/>
                    <input type='number' name='fifth' pattern="[0-9]*" onKeyUp={handleOnKeyUp}
                           onInput={handleOnInput} tabIndex={5}/>
                    <input type='number' name='fourth' pattern="[0-9]*" onKeyUp={handleOnKeyUp}
                           onInput={handleOnInput} tabIndex={4}/>
                    <input type='number' name='third' pattern="[0-9]*" onKeyUp={handleOnKeyUp}
                           onInput={handleOnInput} tabIndex={3}/>
                    <input type='number' name='second' pattern="[0-9]*" onKeyUp={handleOnKeyUp}
                           onInput={handleOnInput} tabIndex={2}/>
                    <input type='number' name='first' pattern="[0-9]*" onKeyUp={handleOnKeyUp}
                           onInput={handleOnInput} tabIndex={1}/>
                </div>

                {!lockDown &&
                    <Fragment>
                        <button
                            type="button"
                            className="send-again"
                            onClick={() => handleResendOtp(false)}
                            disabled={!sendTimer}
                        >
                            {text['phone_validation_send_again']}
                        </button>

                        {canSendEmailOtp && (
                            <button
                                type="button"
                                className="send-again"
                                onClick={handleEmailOtp}
                            >
                                {text['phone_validation_send_again_email'] || text['phone_validation_email']}
                            </button>
                        )}

                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={
                                disableMainBtn ||
                                isLoading ||
                                isValidateOtpLoading ||
                                isRegisterLoading
                            }
                        >
                            {buttonText}
                        </button>

                        {!firstTry && otpError && (
                            <span className="error-massage">
                            {otpError}
                        </span>
                        )}
                    </Fragment>
                }
                {lockDown &&
                    <span className='error-massage'>{lockDown.content}</span>
                }
            </form>
            </>
            )}

        </div>
    );
};

export default PhoneValidation;
